import { format, sub } from 'date-fns';

/**
 * Converts camelCase filter names to user-friendly names
 * @param {string} filterName - The camelCase filter name
 * @returns {string} User-friendly filter name
 */
function formatFilterName(filterName) {
  const filterNameMap = {
    projectPeriod: 'Time Period',
    dateRange: 'Date Range',
    search: 'Search Terms',
    state: 'State',
    country: 'Country',
    sentiment: 'Sentiment',
    tweetType: 'Tweet Type',
    source: 'Source',
    category: 'Category',
    diseaseState: 'Disease State',
    topic: 'Topic',
    treatment: 'Treatment',
    keyword: 'Keywords',
    domain: 'Domain',
    scientificRank: 'Scientific Rank',
    hasTwitterFollowers: 'Has Twitter Followers',
    isScientificallyProfiled: 'Scientifically Profiled',
    isDigitalOpinionLeader: 'Digital Opinion Leader',
    segment: 'Segment',
    twitterBioSearch: 'Twitter Bio Search',
    advancedSearch: 'Advanced Search',
    profile: 'Profile',
    onTopic: 'On Topic',
    list: 'List',
    treatmentsFlag: 'Treatments Flag',
    socialRank: 'Social Rank',
    twitterHandle: 'Twitter Handle',
    author: 'Author',
    mentionedWith: 'Mentioned With',
    mentionedBy: 'Mentioned By',
    collection: 'Collection',
    addedToKwello: 'Added to Kwello',
    projectId: 'Project ID',
    type: 'Type',
    dataStep: 'Data Step',
  };
  return (
    filterNameMap[filterName] ||
    filterName.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())
  );
}
/**
 * Formats filter values to be user-friendly
 * @param {any} value - The filter value
 * @param {string} filterName - The filter name
 * @param {Function} resolveFilterValue - Function to resolve filter values to display names
 * @returns {string} Formatted filter value
 */
function formatFilterValue(value, filterName, resolveFilterValue = null) {
  // Handle null/undefined values
  if (value === null || value === undefined) {
    return 'None';
  }
  // Handle empty arrays
  if (Array.isArray(value) && value.length === 0) {
    return 'None';
  }
  // Handle arrays
  if (Array.isArray(value)) {
    // If we have resolveFilterValue function, use it (this handles ID-to-name resolution)
    if (resolveFilterValue) {
      const resolvedValues = resolveFilterValue(value);
      return Array.isArray(resolvedValues) ? resolvedValues.join(', ') : String(resolvedValues);
    }

    // Fallback to manual formatting if no resolveFilterValue function
    return value
      .map(item => {
        if (typeof item === 'object' && item !== null) {
          // Handle objects with id/name properties
          if (item.id && item.name) {
            return item.name;
          }
          if (item.id) {
            return item.id;
          }
          return JSON.stringify(item);
        }
        return String(item);
      })
      .join(', ');
  }
  // Handle objects
  if (typeof value === 'object' && value !== null) {
    // Special handling for projectPeriod
    if (filterName === 'projectPeriod') {
      const { metric, value: periodValue, direction } = value;
      const directionText = direction === 'backward' ? 'ago' : 'from now';
      return `Last ${periodValue} ${metric}${periodValue > 1 ? 's' : ''} ${directionText}`;
    }
    // Special handling for scientificRank
    if (filterName === 'scientificRank') {
      const { greaterThanOrEqualTo, lessThanOrEqualTo } = value;
      if (greaterThanOrEqualTo !== null && lessThanOrEqualTo !== null) {
        return `${greaterThanOrEqualTo} - ${lessThanOrEqualTo}`;
      }
      if (greaterThanOrEqualTo !== null) {
        return `≥ ${greaterThanOrEqualTo}`;
      }
      if (lessThanOrEqualTo !== null) {
        return `≤ ${lessThanOrEqualTo}`;
      }
      return 'None';
    }
    // Handle other objects
    return JSON.stringify(value);
  }
  // Handle strings and numbers
  return String(value);
}
/**
 * Core function to format filter notes
 *
 * @param {string} introText - Introduction text for the notes
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @param {Object} filters - Filters object
 * @param {Function} resolveFilterValue - Function to resolve filter values to display names
 * @returns {string} Formatted notes text
 */
function formatNotesFilter(introText, startDate, endDate, filters, resolveFilterValue = null) {
  // Handle different date formats - check for direct dates or dateRange object
  let actualStartDate = startDate;
  let actualEndDate = endDate;
  // If no direct dates provided, try to extract from filters.dateRange
  if (!actualStartDate && !actualEndDate && filters?.dateRange) {
    actualStartDate = filters.dateRange.start;
    actualEndDate = filters.dateRange.end;
  }
  // If still no dates and we have projectPeriod, calculate the date range
  if (!actualStartDate && !actualEndDate && filters?.projectPeriod) {
    const now = new Date();
    const { metric, value, direction } = filters.projectPeriod;
    if (direction === 'backward') {
      // For backward, end date is now, start date is value units ago
      actualEndDate = now;

      // Convert metric to plural form for date-fns
      let pluralMetric = metric;
      if (metric === 'month') pluralMetric = 'months';
      else if (metric === 'day') pluralMetric = 'days';
      else if (metric === 'year') pluralMetric = 'years';
      else if (metric === 'week') pluralMetric = 'weeks';

      actualStartDate = sub(now, { [pluralMetric]: value });
    } else if (direction === 'forward') {
      // For forward, start date is now, end date is value units in the future
      actualStartDate = now;

      // Convert metric to plural form for date-fns
      let pluralMetric = metric;
      if (metric === 'month') pluralMetric = 'months';
      else if (metric === 'day') pluralMetric = 'days';
      else if (metric === 'year') pluralMetric = 'years';
      else if (metric === 'week') pluralMetric = 'weeks';

      actualEndDate = sub(now, { [pluralMetric]: -value });
    }
  }
  // Format dates with validation
  const formatDate = date => {
    if (!date) return 'N/A';
    const dateObj = new Date(date);
    return Number.isNaN(dateObj.getTime()) ? 'N/A' : format(dateObj, 'dd-MMM-yy');
  };

  const formattedStart = formatDate(actualStartDate);
  const formattedEnd = formatDate(actualEndDate);
  let notes = `${introText}\n\nDate Range: ${formattedStart} → ${formattedEnd}\n\n`;
  // Handle filters
  if (!filters || typeof filters !== 'object') {
    notes += 'No filters applied.';
    return notes;
  }
  const entries = Object.entries(filters);
  if (!entries.length) {
    notes += 'No filters applied.';
  } else {
    notes += 'Applied Filters:\n';
    notes += entries
      .filter(([key, value]) => {
        // Filter out empty values and system fields
        if (value === null || value === undefined) return false;
        if (Array.isArray(value) && value.length === 0) return false;
        if (typeof value === 'object' && value !== null && Object.keys(value).length === 0)
          return false;
        if (['projectId', 'type', 'dataStep', 'dateRange', 'projectPeriod'].includes(key))
          return false;
        // Filter out values that are effectively empty
        if (typeof value === 'string' && value.trim() === '') return false;
        if (typeof value === 'object' && value !== null) {
          // Check if all values in the object are null/undefined/empty
          const allEmpty = Object.values(value).every(
            v => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0),
          );
          if (allEmpty) return false;
        }
        return true;
      })
      .map(([key, value]) => {
        const friendlyName = formatFilterName(key);
        const friendlyValue = formatFilterValue(value, key, resolveFilterValue);
        return { key, friendlyName, friendlyValue };
      })
      .filter(({ friendlyValue }) => {
        // Hide filters with empty values after processing
        if (!friendlyValue || friendlyValue === 'None' || friendlyValue.trim() === '') {
          return false;
        }
        // Hide if it's just an empty array representation
        if (friendlyValue === '[]') {
          return false;
        }
        // Hide if it's just a colon with no value (e.g., "Profile: ")
        if (friendlyValue.includes(': ') && friendlyValue.split(': ')[1].trim() === '') {
          return false;
        }
        return true;
      })
      .map(({ friendlyName, friendlyValue }) => `  - ${friendlyName}: ${friendlyValue}`)
      .join('\n');
  }
  return notes;
}
/**
 * Formats applied filters into slide notes text
 * Based on NextGen PPTformatNotesFromFilter implementation
 *
 * @param {string} slideTitle - The title of the slide
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @param {Object} filters - Applied filters object
 * @param {Function} resolveFilterValue - Function to resolve filter values to display names
 * @returns {string} Formatted notes text
 */
export const formatNotesFromFilters = (
  slideTitle,
  startDate,
  endDate,
  filters,
  resolveFilterValue = null,
) => {
  return formatNotesFilter(
    `Slide Title: ${slideTitle}`,
    startDate,
    endDate,
    filters,
    resolveFilterValue,
  );
};
/**
 * Formats universal filters into slide notes text
 *
 * @param {string} startDate - Start date in ISO format
 * @param {string} endDate - End date in ISO format
 * @param {Object} filters - Universal filters object
 * @returns {string} Formatted notes text
 */
export const formatNotesFromUniversalFilters = (startDate, endDate, filters) => {
  return formatNotesFilter('\n\nUniversal filters:', startDate, endDate, filters);
};
/**
 * Formats profile social links into slide notes
 *
 * @param {Object} profile - Profile object containing social links
 * @returns {string} Formatted social links notes
 */
export const formatProfileSocialLinks = profile => {
  if (!profile) {
    return 'Profile information not available.';
  }
  let notes = `Profile: ${profile.profileName || 'Unknown Profile'}\n\n`;
  // Social Links section
  const socialLinks = [];
  if (profile.twitterURL) {
    socialLinks.push(`  - Twitter: ${profile.twitterURL}`);
  }
  if (profile.linkedinURL) {
    socialLinks.push(`  - LinkedIn: ${profile.linkedinURL}`);
  }
  if (profile.facebookURL) {
    socialLinks.push(`  - Facebook: ${profile.facebookURL}`);
  }
  if (profile.instagramURL) {
    socialLinks.push(`  - Instagram: ${profile.instagramURL}`);
  }
  if (socialLinks.length > 0) {
    notes += 'Social Links:\n';
    notes += socialLinks.join('\n');
    notes += '\n\n';
  }
  // Bio section
  if (profile.twitterBio) {
    const bioText =
      profile.twitterBio.length > 200
        ? `${profile.twitterBio.substring(0, 200)}...`
        : profile.twitterBio;
    notes += `Bio: ${bioText}`;
  }
  return notes;
};
/**
 * Extracts filter information from exportApiConfig
 *
 * @param {Object} exportApiConfig - Export API configuration object
 * @returns {Object} Extracted filter information
 */
export const extractFiltersFromApiConfig = exportApiConfig => {
  if (!exportApiConfig || !exportApiConfig.data) {
    return {
      startDate: null,
      endDate: null,
      filters: {},
    };
  }
  const { data } = exportApiConfig;

  // Extract date information
  const startDate = data.startDate || data.dateFrom || data.dateRange?.start || null;
  const endDate = data.endDate || data.dateTo || data.dateRange?.end || null;

  // Create filters object, removing date fields but keeping other useful data
  const filters = { ...data };
  delete filters.startDate;
  delete filters.endDate;
  delete filters.dateFrom;
  delete filters.dateTo;
  delete filters.dateRange;

  return {
    startDate,
    endDate,
    filters,
  };
};
